/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, EventEmitter, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import mapLodash from 'lodash-es/map';
import {Toast, ToastrService} from 'ngx-toastr';
import {ActiveToast} from 'ngx-toastr/toastr/toastr.service';
import {BehaviorSubject, EMPTY, forkJoin, from, Observable} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  sampleTime,
  share,
  skip,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';

import {DATE_MAX, REQUEST_OPTIONS} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {PayPeriodContractChange} from '../../inbox/shared/communications.interface';
import {CommunicationsService} from '../../inbox/shared/communications.service';
import {PayPeriodAttributes} from '../../models/models.interfaces';
import {ApiRequestOptions, ApiService, Data} from '../../shared/api';
import {LabelledAndColoredDatum} from '../../shared/charts/d3/chart.interfaces';
import {PayStatus} from '../../shared/employees/employees.model';
import {EmployeesContextUtilsService} from '../../shared/employees/utils/employee-context-utils.service';
import {FiqlService, SearchQuery} from '../../shared/fiql/fiql.service';
import {LocalizationService} from '../../shared/localization/localization.service';
import {ModalService} from '../../shared/modal';
import {CanComponentDeactivate} from '../../shared/routing/can-deactivate-guard';
import {DefaultPagination, Pagination} from '../../shared/ui';
import {SortDirection} from '../../shared/ui/table/table.constants';
import {SortExpression, SortKey} from '../../shared/ui/table/table.interfaces';
import {User, UserService} from '../../shared/user';
import {Enums} from '../../shared/utils/enums';
import {createFiltersObj, getFilterName, updateFiltersOnGoingPayrolls} from '../../shared/utils/payroll-filters';
import {Payrolls} from '../../shared/utils/payrolls';
import {PayResultsService} from '../analyze/pay-results.service';
import {PayTypeContextChange, PayTypeWithCliRegIds} from '../payrolls.model';
import {PayrollUtilsService} from '../utils/payroll-utils.service';

import {
  Action,
  ButtonDetail,
  ButtonInfos,
  OngoingPayrollsResponse,
  OngoingPayrollStatistics,
  Pay,
  PayslipPaymentStatus,
  PayStatusDetail
} from './ongoing-payrolls.interfaces';
import {OngoingPayrollsService} from './ongoing-payrolls.service';
import {RetroChangesComponent} from './retro-changes/retro-changes.component';


export const getDisplayedDataCountForAction = (pays: Pay[], action: Action) =>
  pays.flatMap(pay => pay.actions).filter(act => Action[act] === action).length;

export const flattenSetOfSets = (entries: Set<number>[]) =>
  new Set(entries.reduce((acc, currentValues) => acc.concat([...currentValues]), []));


export const initMap = () => {
  const mapIdsByAction = new Map<Action, Set<number>>();
  Enums.forEachValue<Action>(Action, action => {
    if (action !== Action.READY && action !== Action.CREATE_NEXT_PPC) {
      mapIdsByAction.set(action, new Set<number>());
    }
  });
  return mapIdsByAction;
};

export const hasWhiteSpace = (str: string): boolean => /\s/g.test(str);

export const addQuotesToStringsWithWhiteSpace = (text: string): string =>
  hasWhiteSpace(text) ? '\"' + text + '\"' : text;


/**
 * Ongoing payroll screen : displays ongoing payrolls in a filterable / sortable table, let user execute individual or
 * mass pay actions(launch, freeze, done) on existing pays.
 * Also displays payroll stats in the dashboard-style top part.
 */
@Component({
  selector: 'gp-view-payroll-ongoing',
  templateUrl: './ongoing-payrolls.component.html',
  providers: [
    FiqlService
  ],
  styleUrls: ['./ongoing-payrolls.component.scss']
})
export class OngoingPayrollsComponent extends Destroyable implements OnInit, CanComponentDeactivate {

  public static ACCESS_NAME = 'OngoingPayrolls';
  public PayStatus = PayStatus;
  public PayslipPaymentStatus = PayslipPaymentStatus;

  public countLaunch: number;
  public countFreeze: number;
  public countUnfreeze: number;
  public countDone: number;
  public countLastDone: number;
  public countReady: number;
  public countNext: number;

  public forcedPriority: 'HIGH' | 'MEDIUM' | 'LOW';
  public isForceRetroactivity: boolean = false;
  public isDebuggingMode: boolean = false;
  public retroactivityDate: string;
  public isLaunchAll: boolean = false;
  public isDoneAll: boolean = false;
  public isFreezeAll: boolean = false;
  public isUnfreezeAll: boolean = false;
  public massActionCount: number;
  public user: User;

  // Access to the actions enum from template
  public actions = Action;

  // Access to sort directions from template
  public directions = SortDirection;

  // List of currently displayed pays in the table
  public pays: Array<Pay> = null;

  public dataLoaded: EventEmitter<void> = new EventEmitter<void>();

  // Information about the mass action buttons
  public massActionButtons: ButtonInfos = null;

  // Pagination state
  public pagination: Pagination = new DefaultPagination();

  // Map of actions selected by user and pending execution
  public selectedPPCIdsByActionsMap: Map<Action, Set<number>> = new Map<Action, Set<number>>();

  public ppcIdsByActionsMap: Map<Action, Set<number>> = new Map<Action, Set<number>>();

  public moreActions = [Action.READY, Action.CREATE_NEXT_PPC];

  public executingActions: boolean = false;

  public filters: SearchQuery = [];

  public sortKey: SortKey = [{key: 'lastName,firstName,cliRegId', direction: SortDirection.ASCENDING}];

  // Chart & stats data holders

  public statistics: OngoingPayrollStatistics;

  public payrollStatusChart: { [key: string]: any };
  public payrollHeaderChartData: Array<LabelledAndColoredDatum> = [];
  public payrollHeaderChartReady: boolean;

  public payPeriods$: Observable<Array<Data<PayPeriodAttributes>>>;
  public filtersVisible: boolean;

  public allSelectionToggled = false;

  public selectedAction: Action;

  public filtersGroup: UntypedFormGroup;
  public isPayTypeFilterDisabled: boolean = false;

  public payTypes: PayTypeWithCliRegIds[] = [];

  public payPeriodContractIds: number[] = [];

  public triggerSortByTranslationPipe: boolean = true;
  public statusTypes = [
    {name: 'PAYROLL_ONGOING.done', key: 'DONE'},
    {name: 'PAYROLL_ONGOING.todo', key: 'TODO'},
    {name: 'PAYROLL_ONGOING.error', key: 'ERROR'},
    {name: 'PAYROLL_ONGOING.scheduled', key: 'SCHEDULED'},
    {name: 'PAYROLL_ONGOING.again', key: 'AGAIN'},
    {name: 'PAYROLL_ONGOING.frozen', key: 'FROZEN'},
    {name: 'PAYROLL_ONGOING.alert', key: 'ALERT'},
    {name: 'PAYROLL_ONGOING.ongoing', key: 'ONGOING'}
  ];

  public actionTypes = [
    {name: 'PAYROLL_ONGOING.launch', key: 'LAUNCH', enumValue: Action.LAUNCH},
    {name: 'PAYROLL_ONGOING.done', key: 'DONE', enumValue: Action.DONE},
    {name: 'PAYROLL_ONGOING.freeze', key: 'FREEZE', enumValue: Action.FIX},
    {name: 'PAYROLL_ONGOING.unfreeze', key: 'UNFREEZE', enumValue: Action.UNFREEZE}
  ];

  public moreActionTypes = [Action.READY, Action.CREATE_NEXT_PPC];

  public isSingleActionToggled = false;

  public gpSelectOptions = [
    {
      action: Action.LAUNCH,
      label: 'PAYROLL_ONGOING.launch_selected',
      cssClass: 'text-info',
      executeAll: this.isLaunchAll
    },
    {
      action: Action.DONE,
      label: 'PAYROLL_ONGOING.done_selected',
      cssClass: 'text-warning',
      executeAll: this.isDoneAll
    },
    {
      action: Action.FIX,
      label: 'PAYROLL_ONGOING.freeze_selected',
      cssClass: 'text-success',
      executeAll: this.isFreezeAll
    },
    {
      action: Action.UNFREEZE,
      label: 'PAYROLL_ONGOING.unfreeze_selected',
      cssClass: 'text-primary',
      executeAll: this.isUnfreezeAll
    }
  ];
  public withStatistics: boolean = false;
  public triggerDateFormat$: Observable<number>;
  private payPeriodContractChangesToast: ActiveToast<Toast>;
  private triggerContextPayType$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private toastrSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private selectedPay: Pay;

  constructor(private ongoingPayrollsService: OngoingPayrollsService,
              private fiql: FiqlService,
              private modalService: ModalService,
              private router: Router,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private localization: LocalizationService,
              private userService: UserService,
              private employeesContextService: EmployeesContextUtilsService,
              private payResultsService: PayResultsService,
              private formBuilder: UntypedFormBuilder,
              private communicationsService: CommunicationsService,
              private payrollUtilsService: PayrollUtilsService,
              private toastrService: ToastrService,
              private api: ApiService) {
    super();
  }

  public ngOnInit() {
    const initialContextPayType = this.employeesContextService.getContextPayType();
    const initialQueryPayType: string = this.route.snapshot.queryParamMap.get('payTypeTransId');
    let payTypeTransId: string = '';
    if (initialContextPayType && (initialContextPayType.transId === '' || initialContextPayType.transId === initialQueryPayType)) {
      payTypeTransId = initialQueryPayType;
    } else if (initialContextPayType && initialContextPayType.transId) {
      payTypeTransId = initialContextPayType.transId;
      this.isPayTypeFilterDisabled = initialContextPayType.disablePayTypeFilter;
    }

    this.filtersGroup = this.formBuilder.group({
      payTypeTransId: [payTypeTransId],
      contractPeriodId: [''],
      name: [''],
      externalNumber: [''],
      status: [''],
      retroActivityCalculationCount: [''],
      action: ['']
    });

    this.filtersGroup.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(400),
        tap(() => this.update())
      )
      .subscribe();

    this.resetSelectedActionsMap();

    this.route.queryParams
      .pipe(
        tap((params: Params) => {
          this.sortKey = params.sort
            ? [this.ongoingPayrollsService.createSortExpression(params.sort)]
            : [{key: 'lastName,firstName,cliRegId', direction: SortDirection.ASCENDING}];

          this.filtersGroup.setValue({
            payTypeTransId: params.payTypeTransId || this.filtersGroup.get('payTypeTransId').value || '',
            contractPeriodId: params.contractPeriodId || '',
            name: params.name || '',
            externalNumber: params.externalNumber || '',
            status: params.status || '',
            retroActivityCalculationCount: params.retroActivityCalculationCount || '',
            action: params.action || ''
          });
        }),
        switchMap((params: Params) => {
          if (!isEqual(params, {})) {
            const filters = this.filtersGroup.value;
            Object.keys(filters).forEach((key: string) => this.updateFilter(getFilterName(key), filters[key]));
            return this.fetchPayrolls(true);
          }
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.getPayPeriodContractChangesToast$().subscribe();
    this.getContextPayType$().subscribe();
    this.fetchPayTypes$().subscribe();

    this.userService
      .getUser()
      .pipe(
        tap((user: User) => this.user = user),
        skip(1),
        switchMap(() => this.withStatistics ? this.fetchAndPrepareReportingData() : EMPTY),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.triggerDateFormat$ = this.localization.changeDateFormatEvent.pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
    this.pagination.pageChanged
      .asObservable()
      .pipe(
        switchMap(() => this.fetchPayrolls(true)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.payPeriods$ = this.ongoingPayrollsService.listOngoingPayPeriods();

    this.translate.onLangChange.pipe(
      tap(() => this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.triggerDateFormat$ = this.localization.changeDateFormatEvent.pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );

    this.communicationsService.payPeriodContractChange
      .pipe(
        sampleTime(5000),
        switchMap((payPeriodContractChanges: PayPeriodContractChange[]) => {
          if (payPeriodContractChanges && payPeriodContractChanges.length) {
            const changeTypes = mapLodash(payPeriodContractChanges, 'payPeriodContractChangeType');
            if ((changeTypes.includes('CREATE') || changeTypes.includes('DELETE')) && !this.toastrService.currentlyActive) {
              this.payPeriodContractChangesToast = this.toastrService
                .info(this.translate.instant('DIALOG.pay_period_contracts_changed'), null, {timeOut: 10000});
              this.toastrSubject$.next(true);
            } else if (changeTypes.includes('UPDATE')) {
              const updatedPayPeriodContractIds = payPeriodContractChanges
                .filter(payPeriodContractChange => payPeriodContractChange.payPeriodContractChangeType === 'UPDATE')
                .map(payPeriodContractChange => payPeriodContractChange.payPeriodContractId);
              const payPeriodContractIds = this.pays.map(pay => pay.id);
              if (payPeriodContractIds.some(id => updatedPayPeriodContractIds.includes(id))) {
                return this.fetchPayrolls(false);
              }
            }
          }
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

  }

  // TODO : to change hard coded value after clarification
  private fetchPayPeriodContractIds(): Observable<{ id: number, actions: string[] }[]> {
    const opts: ApiRequestOptions = REQUEST_OPTIONS.OBSERVE.RESPONSE;
    opts.requestOptions.params = {
      'per-page': 999999,
      'page': 0,
      'order-by': ['lastName', 'firstName', 'cliRegId']
    };

    const tempFilter = cloneDeep(this.filters);

    const filters = this.fiql.serialize(tempFilter);

    let search = 'payState.status!=CLOSED;payState.status!=FUTURE';
    if (filters && filters.length !== 0) {
      search += `;${filters}`;
    }
    opts.requestOptions.params.search = search;
    this.payPeriodContractIds = [];
    return this.ongoingPayrollsService.listAllPayPeriodContract(opts)
      .pipe(
        tap((pays: Pay[]) => {
          this.ppcIdsByActionsMap = this.groupPayIdByAction(pays);
        }));

  }

  private groupPayIdByAction(pays: Pay[]): Map<Action, Set<number>> {
    const tempMap = initMap();
    pays.forEach(pay => {
      if (pay.actions.length === 0) {
        tempMap.get(Action.NONE).add(pay.id);
      } else {
        pay.actions.forEach(action => {
          if (this.actionTypes.find(act => act.enumValue === Action[action])) {
            tempMap.get(Action[action]).add(pay.id);
          } else {
            tempMap.get(Action.NONE).add(pay.id);
          }
        });
      }
    });
    return tempMap;
  }

  private updatePPContract(id: number, actions: string[], ppcIdsByActionsMap: Map<Action, Set<number>>): Map<Action, Set<number>> {
    const tempMap = cloneDeep(ppcIdsByActionsMap);
    if (actions.length === 0) {
      tempMap.get(Action.NONE).add(id);
    } else {
      actions.forEach(action => {
        if (this.actionTypes.find(act => act.enumValue === Action[action])) {
          tempMap.get(Action[action]).add(id);
        } else {
          tempMap.get(Action.NONE).add(id);
        }
      });
    }
    return tempMap;
  }


  /**
   * Intercept router deactivation so that we can warn user when they have selected actions pending execution
   */
  public canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasSelectedActions()) {
      return true;
    }

    return from(
      this.modalService.confirm({
        'title': 'DIALOG.сancel_pending_selection',
        'text': 'DIALOG.cancel_current_selection?',
        'type': 'warning'
      }).then((confirmed: boolean) => {
        if (confirmed) {
          this.resetSelectedActionsMap();
        }

        return confirmed;
      })
    );
  }

  public getFilterName(key: string): string {
    let result: string;

    switch (key) {
      case 'contractPeriodId':
        result = 'contractPeriod.id';
        break;
      case 'status':
        result = 'payState.status';
        break;
      case 'retroActivityCalculationCount':
        result = 'payState.retroActivityCalculationCount';
        break;
      case 'action':
        result = 'action';
        break;
      default:
        result = key;
        break;
    }

    return result;
  }

  public update(): void {
    const sort = this.sortKey.map(element => {
      const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
      return `${prefix}${element.key}`;
    }).join(',');

    const queryParams = {
      ...createFiltersObj(this.filtersGroup),
      sort
    };

    if (!isEqual(queryParams, this.route.snapshot.queryParams)) {
      this.router.navigate(['payroll/calculate'], {queryParams});
    }
  }

  /**
   * Reset the selected actions : discard all user selected actions pending execution
   */
  public resetSelectedActionsMap() {
    Enums.forEachValue<Action>(Action, action => this.selectedPPCIdsByActionsMap.set(action, new Set<number>()));
  }

  public resetPayPeriodContractByActionsMap() {
    Enums.forEachValue<Action>(Action, action => {
      if (action !== Action.READY && action !== Action.CREATE_NEXT_PPC) {
        this.ppcIdsByActionsMap.set(action, new Set<number>());
      }
    });
  }

  public resetSelections() {
    this.resetSelectedActionsMap();
    this.allSelectionToggled = false;
    this.resetPayPeriodContractByActionsMap();
    this.resetOperationCounts();
    this.pays.forEach(pay => pay.selected = false);
  }

  /**
   * Fetch reporting statistics and prepare data for graphs in header
   */
  public fetchAndPrepareReportingData(): Observable<OngoingPayrollStatistics> {
    this.payrollHeaderChartReady = false;
    return this.ongoingPayrollsService.getStatistics()
      .pipe(
        tap((statistics: OngoingPayrollStatistics) => {
          this.statistics = statistics;

          this.payrollHeaderChartData = statistics.payrollStatus.detail
            .reduce(this.accumulateHeaderChartData, this.getEmptyHeaderChartData());
          this.payrollHeaderChartReady = true;

          this.payrollStatusChart = this.ongoingPayrollsService.getPayrollStatusChart(statistics);
        })
      );
  }

  /**
   * Fetch payrolls against the API, according to current filters, sorting and pagination state
   */
  public fetchPayrolls(scrollToOrigin: boolean = true): Observable<OngoingPayrollStatistics> {
    if (scrollToOrigin) {
      window.scrollTo(0, 0);
    }
    this.dataLoaded.next(null);
    const search: string = this.fiql.serialize(this.filters),
      sort: string = this.sortKey.map(element => {
        const prefix = element.direction === SortDirection.DESCENDING ? '-' : '';
        return `${prefix}${element.key}`;
      }).join(',');

    this.pagination.resetPageIfNeeded({search, sort});

    return this.ongoingPayrollsService.fetchPayrolls(this.pagination.perPage, this.pagination.currentPage.number, search, sort)
      .pipe(
        tap((response: OngoingPayrollsResponse) => {
          this.pays = [];
          this.pays = response.payload.datas;
          this.massActionButtons = response.payload.buttonAllInfos;
          this.pagination.update(response.total);
          this.updateSelectedPays();
        }),
        switchMap(() => this.fetchPayPeriodContractIds()),
        switchMap(() => this.withStatistics ? this.fetchAndPrepareReportingData() : EMPTY),
        finalize(() => {
          this.pays = this.pays || [];
          this.dataLoaded.next(null);
          this.executingActions = false;
          this.isFreezeAll = false;
          this.isUnfreezeAll = false;
          this.isLaunchAll = false;
          this.isDoneAll = false;
          this.isSingleActionToggled = false;
          this.selectedPay = null;
        })
      );
  }

  public resetTable() {
    this.allSelectionToggled = false;
    this.resetSelectedActionsMap();
    this.resetPayPeriodContractByActionsMap();
    this.resetOperationCounts();
    this.fetchPayrolls(false).subscribe();
  }

  public updateSelectedPays() {
    if (this.hasSelectedActions()) {
      const setOfSelectedIds = Array.from(this.selectedPPCIdsByActionsMap.values());
      const selectedPayIds = flattenSetOfSets(setOfSelectedIds);
      this.pays = this.pays.map(pay => ({...pay, selected: selectedPayIds.has(pay.id)}));
    }
  }

  /**
   * Toggle the sorting on the key for the passed column
   * @param key the key to toggle column sorting for
   */
  public toggleColumnSort(key: string) {
    const existing: SortExpression = this.sortKey.find(k => k.key === key);
    if (existing) {
      existing.direction = existing.direction === SortDirection.ASCENDING ?
        SortDirection.DESCENDING : SortDirection.ASCENDING;
    } else {
      this.sortKey = [{key, direction: SortDirection.ASCENDING}];
    }
    this.update();
  }

  /**
   * Get active column sorting direction for the passed column key
   * @param key the key to get sorting direction for
   * @returns {SortDirection | null} the sort direction if the key is as active sort key, null otherwise
   */
  public getColumnSortDirection(key: string) {
    const existing: SortExpression = this.sortKey.find(k => k.key === key);
    return existing ? existing.direction : null;
  }

  /**
   * Checks if a specific filter is set to specific value
   * @param name
   * @param value
   * @param operator
   * @returns {boolean}
   */
  public isFilterSet(name: string, value: string, operator?: string): boolean {
    return this.filters.some(
      currentFilter => currentFilter.name === name
        && currentFilter.value === value
        && (!operator || currentFilter.operator === operator));
  }

  /**
   * Updates a filter (possibly create it) with a valueFilter and operator.
   * @param nameFilter the name of the filter (FIQL left-hand)
   * @param valueFilter the valueFilter of the filter (FIQL right-hand)
   * @param operator the operator to apply (FIQL operator)
   */
  public updateFilter(nameFilter: string, valueFilter: string = '', operator: string = '==') {
    updateFiltersOnGoingPayrolls(this.filters, nameFilter, valueFilter, operator);
    this.update();
  }

  public executeSelectedConfirm() {
    this.modalService.closeTopmostModal();
    if (this.isLaunchAll && !this.isSingleActionToggled) {
      this.launchAllConfirm();
      return;
    }
    const actions: Array<Observable<void>> = [];
    if (this.selectedAction === Action.LAUNCH) {
      const launchIds = this.isSingleActionToggled ?
        [this.selectedPay.id] :
        Array.from(this.selectedPPCIdsByActionsMap.get(Action.LAUNCH).values());
      if (launchIds.length > 0) {
        launchIds.forEach(id => {
          const foundPay = this.pays.find(pay => pay.id === id);
          if (foundPay) {
            foundPay.status = PayStatus.SENDED;
          }
        });
        actions.push(this.ongoingPayrollsService.launch(
          launchIds,
          this.isForceRetroactivity,
          this.isDebuggingMode,
          this.retroactivityDate,
          this.forcedPriority
        ));
        this.clearRetroactivity();
      }
    }
    if (this.selectedAction !== Action.LAUNCH) {
      const fixIds = this.getExecutedActionsIds(Action.FIX),
        unfrozenIds = this.getExecutedActionsIds(Action.UNFREEZE),
        doneIds = this.getExecutedActionsIds(Action.DONE),
        lastDoneIds = this.getExecutedActionsIds(Action.LAST_DONE),
        readyIds = this.getExecutedActionsIds(Action.READY),
        nextIds = this.getExecutedActionsIds(Action.CREATE_NEXT_PPC);

      if (fixIds.length > 0
        || unfrozenIds.length > 0
        || doneIds.length > 0
        || lastDoneIds.length > 0
        || readyIds.length > 0
        || nextIds.length > 0) {
        actions.push(this.ongoingPayrollsService.changeStatus(doneIds, fixIds, unfrozenIds, lastDoneIds, readyIds, nextIds));
      }

    }
    if (actions.length > 0) {
      this.executingActions = true;
      forkJoin(actions)
        .pipe(
          tap(() => {
            this.payPeriods$ = this.ongoingPayrollsService.listOngoingPayPeriods();
            this.resetSelectedActionsMap();
          }),
          switchMap(() => this.fetchPayrolls(true)),
          catchError(() => {
            this.executingActions = false;
            return EMPTY;
          })
        )
        .subscribe();
    } else {
      this.fetchPayrolls(true).subscribe();
    }
  }

  public executeSelectedCancel() {
    this.isLaunchAll = false;
    this.isSingleActionToggled = false;
    this.selectedPay = null;
    this.clearRetroactivity();
    this.modalService.closeTopmostModal();
  }

  public clearRetroactivity() {
    this.isForceRetroactivity = false;
    this.isDebuggingMode = false;
    this.retroactivityDate = '';
    this.forcedPriority = null;
  }

  /**
   * Launch all payrolls. Asks for user confirmation.
   */
  public launchSelectedPays() {
    this.isLaunchAll = this.allSelectionToggled;
    if (!this.allSelectionToggled) {
      this.countLaunch = Array.from(this.selectedPPCIdsByActionsMap.get(Action.LAUNCH).values()).length;
    }
  }

  public doneSelectedPays() {
    this.isDoneAll = this.allSelectionToggled;
    if (!this.allSelectionToggled) {
      this.countDone = Array.from(this.selectedPPCIdsByActionsMap.get(Action.DONE).values()).length;
    }
    if (this.isDoneAll) {
      this.doneAll();
      return;
    }
  }

  public freezeSelectedPays() {
    this.isFreezeAll = this.allSelectionToggled;
    if (!this.allSelectionToggled) {
      this.countFreeze = Array.from(this.selectedPPCIdsByActionsMap.get(Action.FIX).values()).length;
    }
    if (this.isFreezeAll) {
      this.freezeAll();
      return;
    }
  }

  public unfreezeSelectedPays() {
    this.isUnfreezeAll = this.allSelectionToggled;
    if (!this.allSelectionToggled) {
      this.countUnfreeze = Array.from(this.selectedPPCIdsByActionsMap.get(Action.UNFREEZE).values()).length;
    }
    if (this.isUnfreezeAll) {
      this.unfreezeAll();
      return;
    }
  }

  public launchAllConfirm() {
    this.executingActions = true;
    this.pays.forEach(pay => pay.status = PayStatus.SENDED);
    this.clearSelectionDetails();
    this.ongoingPayrollsService.launchAll(
      this.fiql.serialize(this.filters),
      this.isForceRetroactivity,
      this.isDebuggingMode,
      this.retroactivityDate,
      this.forcedPriority)
      .pipe(
        tap(() => this.allSelectionToggled = false),
        switchMap(() => this.fetchPayrolls(true)),
        catchError(() => {
          this.executingActions = false;
          this.isLaunchAll = false;
          return EMPTY;
        })
      )
      .subscribe();
    this.clearRetroactivity();
    this.modalService.closeTopmostModal();
  }

  /**
   * Done all payrolls. Asks for user confirmation.
   */


  public doneAll() {
    this.isDoneAll = this.allSelectionToggled;
    if (this.isDoneAll) {
      this.selectedAction = Action.DONE;
      this.modalService.confirm({
        'title': 'FORMS.confirm',
        'text': `${this.translate.instant('PAYROLL_ONGOING.done_all_', {
          massActionCount: this.massActionCount
        })} ${this.translate.instant('COMMON.payrolls')}`
      }).then((confirmed: boolean) => {
        if (confirmed) {
          this.executingActions = true;
          this.clearSelectionDetails();
          this.ongoingPayrollsService.doneAll(this.fiql.serialize(this.filters))
            .pipe(
              tap(() => this.allSelectionToggled = false),
              switchMap(() => this.fetchPayrolls(true)),
              catchError(() => {
                this.executingActions = false;
                this.isDoneAll = false;
                return EMPTY;
              })
            ).subscribe();
        } else {
          this.isDoneAll = false;
        }
      });
    }
  }

  /**
   * Freeze all payrolls. Asks for user confirmation.
   */
  private freezeToggledPay() {
    this.modalService.confirm({
      'title': 'FORMS.confirm',
      'text': `${this.translate.instant('PAYROLL_ONGOING.freeze_selected_confirmation', {
        employeeName: this.selectedPay.firstName + ' ' + this.selectedPay.lastName,
        contractId: this.selectedPay.contractID
      })}`
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.changeStatus();
      } else {
        this.isSingleActionToggled = false;
      }
    });
  }

  private unfreezeToggledPay() {
    this.modalService.confirm({
      'title': 'FORMS.confirm',
      'text': `${this.translate.instant('PAYROLL_ONGOING.unfreeze_selected_confirmation', {
        employeeName: this.selectedPay.firstName + ' ' + this.selectedPay.lastName,
        contractId: this.selectedPay.contractID
      })}`
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.changeStatus();
      } else {
        this.isSingleActionToggled = false;
      }
    });
  }

  public freezeAll() {
    this.isFreezeAll = true;
    this.selectedAction = Action.FIX;
    this.modalService.confirm({
      'title': 'FORMS.confirm',
      'text': `${this.translate.instant('PAYROLL_ONGOING.freeze_all_', {
        massActionCount: this.massActionCount
      })} ${this.translate.instant('COMMON.payrolls')}`
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.executingActions = true;
        this.clearSelectionDetails();
        this.ongoingPayrollsService.freezeAll(this.fiql.serialize(this.filters, ';', true))
          .pipe(
            tap(() => this.allSelectionToggled = false),
            switchMap(() => this.fetchPayrolls(true)),
            catchError(() => {
              this.executingActions = false;
              this.isFreezeAll = false;
              return EMPTY;
            })
          )
          .subscribe();
      } else {
        this.isFreezeAll = false;
      }
    });
  }

  public unfreezeAll() {
    this.isUnfreezeAll = true;
    this.selectedAction = Action.UNFREEZE;
    this.modalService.confirm({
      'title': 'FORMS.confirm',
      'text': `${this.translate.instant('PAYROLL_ONGOING.unfreeze_all_', {
        massActionCount: this.massActionCount
      })} ${this.translate.instant('COMMON.payrolls')}`
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.executingActions = true;
        this.clearSelectionDetails();
        this.ongoingPayrollsService.unfreezeAll(this.fiql.serialize(this.filters, ';', true))
          .pipe(
            tap(() => this.allSelectionToggled = false),
            switchMap(() => this.fetchPayrolls(true)),
            catchError(() => {
              this.executingActions = false;
              this.isUnfreezeAll = false;
              return EMPTY;
            })
          )
          .subscribe();
      } else {
        this.isUnfreezeAll = false;
      }
    });
  }

  /**
   * @returns {boolean} true when there is a least one selected action, false otherwise
   */
  public hasSelectedActions(): boolean {
    return Array.from(this.selectedPPCIdsByActionsMap.values()).filter(value => value.size > 0).length > 0;
  }

  public hasAnyExecutableActionSelected(): boolean {
    const tempMap = cloneDeep(this.selectedPPCIdsByActionsMap);
    tempMap.delete(Action.NONE);
    return Array.from(tempMap.values()).filter(value => value.size > 0).length > 0;
  }

  /**
   * Checks whether an action can be executed for a pay
   *
   * @param pay the pay to check if the action can be executed for
   * @param action the action to check
   * @returns {boolean} true if the action can be executed, false otherwise
   */
  public canDo(pay: Pay, action: Action) {
    return pay.actions.indexOf(Action[action]) >= 0;
  }


  /**
   * Checks if an action is selected on a pay
   * @param pay the pay to check on
   * @param action the action to check
   * @returns {boolean} true if the action is selected on the pay, false otherwise
   */
  public isSelectedFor(pay: Pay, action: Action): boolean {
    if (action === Action.READY || action === Action.CREATE_NEXT_PPC) {
      return false;
    }
    return this.selectedPPCIdsByActionsMap.get(action).has(pay.id);
  }

  /**
   * Checks whether a pay has a payslip available on its last execution.
   *
   * @param pay the pay to check on
   * @returns {boolean} true if the pay has a payslip on its last execution, false otherwise
   */
  public hasPayslip(pay: Pay): boolean {
    return !!this.getPayslipHtmlFileId(pay);
  }

  public hasContractEndDate(pay: Pay): boolean {
    return !!pay.contractEndDate && pay.contractEndDate !== DATE_MAX;
  }

  /**
   * Get the html file id of payslip for a pay
   * @param pay the pay to get the payslip link for
   * @returns the payslipHtmlFileId if found, or null otherwise
   */
  public getPayslipHtmlFileId(pay: Pay): number {
    if (!pay.lastExecution) {
      return null;
    }
    return pay.lastExecution.payslipHtmlFileId;
  }

  /**
   * Get the number of elements for a mass action (launch, done, freeze)
   * @param action {Action} the action for which to get the count
   * @returns {number} the count of elements for that action
   */
  public getSelectedActionCount(action ?: Action): number {
    return this.selectedPPCIdsByActionsMap.get(action).size;
  }

  public openPayEvents(pay: Pay, type1: string = '', type2: string = '') {
    this.router.navigate(['payroll/calculation-events'], {
      queryParams: {
        payTypeTransId: pay.payTypeTransId,
        name: pay.lastName,
        eventType: [type1, type2]
      }
    });
  }

  public getFlagLink(pay: Pay): string {
    return this.api.apiHost + pay.links[0].href;
  }

  // ------------------------------------------------------------------------------------------------------------------

  private accumulateHeaderChartData(result: any, detail: PayStatusDetail) {
    if (['FIX', 'DONE'].indexOf(detail.type) >= 0) {
      return result;
    }
    const index = ['ALERT', 'ERROR'].indexOf(detail.type) >= 0 ? 1 : 0;
    result[index].value += detail.count;
    return result;
  }

  private getEmptyHeaderChartData() {
    return [
      {
        label: Payrolls.getDisplayNameForStatus('TODO', this.translate),
        value: 0,
        color: Payrolls.getColorForStatus('TODO')
      }, {
        label: Payrolls.getDisplayNameForStatus('ALERT', this.translate),
        value: 0,
        color: Payrolls.getColorForStatus('ALERT')
      }
    ];
  }

  public getPayrollStatus(status: string) {
    return Payrolls.getDisplayNameForStatus(status, this.translate);
  }

  public openPay(pay: Pay) {
    this.ongoingPayrollsService.updateLastExecutionId(pay.lastExecution.id);
    this.router.navigate(['payroll/analyze', pay.contractID, {tab: 'Payslip'}]);
  }

  public openOverview(pay: Pay) {
    this.ongoingPayrollsService.updateLastExecutionId(pay.lastExecution.id);
    this.router.navigate(['payroll/analyze', pay.contractID, {tab: 'Overview'}]);
  }

  public openResult(pay: Pay) {
    this.ongoingPayrollsService.updateLastExecutionId(pay.lastExecution.id);
    this.router.navigate(['payroll/analyze', pay.contractID, {tab: 'Payroll result'}]);
  }

  public openEmployeeFile(pay: Pay) {
    this.router.navigate(['/employees/files', pay.contractID]);
  }

  public isAgain(pay: Pay): boolean {
    return pay.status === PayStatus.AGAIN;
  }

  public isUnfreezeUnavailableInCurrentPeriod(pay: Pay): boolean {
    return pay.status === PayStatus.FROZEN && this.isPaymentGenerated(pay);
  }

  public isUnfreezeUnavailableInFuturePeriod(pay: Pay): boolean {
    return pay.status === PayStatus.FROZEN && !this.isPaymentGenerated(pay) && !this.canDo(pay, Action.UNFREEZE);
  }

  private isPaymentGenerated(pay: Pay): boolean {
    return pay.paymentStatus === PayslipPaymentStatus.IN_PROGRESS
      || pay.paymentStatus === PayslipPaymentStatus.SENT
      || pay.payslipStatus === PayslipPaymentStatus.IN_PROGRESS
      || pay.payslipStatus === PayslipPaymentStatus.SENT;
  }

  public viewRetroChanges(event: Event, pay: Pay) {
    event.preventDefault();
    event.stopPropagation();
    this.payResultsService
      .getPayExecRetroChanges(pay)
      .pipe(
        switchMap(response => {
          if (response) {
            return from(this.modalService.openModalWithName('RetroChangesComponent'))
              .pipe(
                tap((modalRef: any) => {
                  modalRef.instance.contractChangesData = response;
                }));
          } else {
            this.modalService.alert({
              title: this.translate.instant('PAYROLL_ONGOING.retro_title'),
              text: this.translate.instant('PAYROLL_ONGOING.retro_error'),
              detail: new Error(this.translate.instant('PAYROLL_ONGOING.retro_error_detail', {payType: pay.payTypeTransId}))
            });
            return EMPTY;
          }
        })
      )
      .subscribe();
  }

  private fetchPayTypes$(): Observable<PayTypeWithCliRegIds[]> {
    return this.payrollUtilsService.payTypesSorted$
      .pipe(
        filter(response => response !== null),
        tap((response: PayTypeWithCliRegIds[]) => {
          this.payTypes = response;
          this.triggerContextPayType$.next(true);
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private getContextPayType$(): Observable<PayTypeContextChange> {
    return this.triggerContextPayType$.pipe(
      distinctUntilChanged(),
      filter(Boolean),
      switchMap(() => this.employeesContextService.listenContextPayType()
        .pipe(
          tap((newPayType: PayTypeContextChange) => {
            this.isPayTypeFilterDisabled = newPayType.disablePayTypeFilter;
            this.filtersGroup.patchValue({payTypeTransId: newPayType.transId});
          })
        )),
      takeUntil(this.componentDestroyed$)
    );
  }

  private getPayPeriodContractChangesToast$(): Observable<OngoingPayrollStatistics> {
    return this.toastrSubject$.pipe(
      filter(Boolean),
      switchMap(() => this.payPeriodContractChangesToast.onTap
        .pipe(
          take(1),
          switchMap(() => this.fetchPayrolls(false))
        ))
    );
  }

  public selectPay(event: Event, pay: Pay) {
    pay.selected = !!(<HTMLInputElement>event.target).checked;
    if (pay.selected) {
      this.selectedPPCIdsByActionsMap = this.updatePPContract(pay.id, pay.actions, this.selectedPPCIdsByActionsMap);
    } else {
      this.allSelectionToggled = false;
      this.unSelectActionsFromSelectedPay(pay.id);
    }
  }


  public isAllChecked() {
    const payPeriodContractIdSets = Array.from(this.ppcIdsByActionsMap.values());
    const payPeriodContractIds = new Set(payPeriodContractIdSets.reduce((a, c) => a.concat([...c]), []));

    const selectedIdsSet = Array.from(this.selectedPPCIdsByActionsMap.values());

    const selectedIds = flattenSetOfSets(selectedIdsSet);

    return this.hasSelectedActions() && ([...payPeriodContractIds].every(value => selectedIds.has(value)));
  }

  public selectAllPays(event: Event) {
    this.allSelectionToggled = !!(<HTMLInputElement>event.target).checked;
    if (this.allSelectionToggled) {
      this.selectedPPCIdsByActionsMap = cloneDeep(this.ppcIdsByActionsMap);
    }
    this.pays.forEach(pay => {
      pay.selected = this.allSelectionToggled;
      if (!this.allSelectionToggled) {
        this.resetSelectedActionsMap();
      }
    });


  }

  public getSelectedActionsCount(action: Action) {
    return this.getSelectedActionCount(action);
  }

  public getMassActionCount(action: Action) {
    const buttonDetail = this.massActionButtons.detail.find((a: ButtonDetail) => Action[a.type] === action);
    return buttonDetail ? buttonDetail.count : null;
  }

  public isExecutionInProgress(pay: Pay, action: Action, massAction?: boolean) {
    return this.executingActions && this.selectedAction === action
      && ((this.isSingleActionToggled && this.selectedPay.id === pay.id)
        || (this.isSelectedFor(pay, action) || massAction));
  }

  public executeAction(action?: Action): void {
    this.selectedAction = this.hasMoreThanOneActionTypeSelected() ? action : this.getSingleSelectedAction();
    if (this.selectedAction !== null) {
      this.massActionCount = this.getSelectedActionsCount(this.selectedAction);
      this.resetOperationCounts();
      if (this.selectedAction === Action.LAUNCH) {
        this.launchSelectedPays();
        this.modalService.openModalWithId('launchConfirm');
        return;
      } else if (this.selectedAction === Action.DONE) {
        this.doneSelectedPays();
      } else if (this.selectedAction === Action.FIX) {
        this.freezeSelectedPays();
      } else if (this.selectedAction === Action.UNFREEZE) {
        this.unfreezeSelectedPays();
      } else if (this.selectedAction === Action.CREATE_NEXT_PPC) {
        this.countNext = Array.from(this.selectedPPCIdsByActionsMap.get(Action.CREATE_NEXT_PPC).values()).length;
      } else {
        this.countReady = Array.from(this.selectedPPCIdsByActionsMap.get(Action.READY).values()).length;
      }
      if (!this.allSelectionToggled || this.selectedAction === Action.READY || this.selectedAction === Action.CREATE_NEXT_PPC) {
        this.modalService.openModalWithId('launchConfirm');
      }
    }
  }

  public isIndeterminate(): boolean {
    return !this.isAllChecked() && this.hasSelectedActions();
  }

  public getSelectAllCheckBoxToolTip(): string {
    return this.isAllChecked() ? 'PAYROLL_ONGOING.unselect_all' : 'PAYROLL_ONGOING.select_all';
  }

  public executeSingleAction(pay: Pay, action: Action): void {
    this.isSingleActionToggled = true;
    this.selectedPay = pay;
    this.selectedAction = action;
    this.updateRelevantCountBasedOnAction(action, 1);
    if (action === Action.LAUNCH) {
      this.modalService.openModalWithId('launchConfirm');
    } else if (action === Action.FIX) {
      this.freezeToggledPay();
    } else if (action === Action.UNFREEZE) {
      this.unfreezeToggledPay();
    } else {
      this.changeStatus();
    }
  }

  public changeStatus() {
    this.isSingleActionToggled = true;
    this.executingActions = true;
    const actions: Array<Observable<void>> = [];
    const fixIds = this.getExecutedActionsIds(Action.FIX),
      unfreezeIds = this.getExecutedActionsIds(Action.UNFREEZE),
      doneIds = this.getExecutedActionsIds(Action.DONE),
      lastDoneIds = this.getExecutedActionsIds(Action.LAST_DONE),
      readyIds = this.getExecutedActionsIds(Action.READY),
      nextIds = this.getExecutedActionsIds(Action.CREATE_NEXT_PPC);

    if (fixIds.length > 0
      || unfreezeIds.length > 0
      || doneIds.length > 0
      || lastDoneIds.length > 0
      || readyIds.length > 0
      || nextIds.length > 0) {

      actions.push(this.ongoingPayrollsService.changeStatus(doneIds, fixIds, unfreezeIds, lastDoneIds, readyIds, nextIds));
      if (actions.length > 0) {
        forkJoin(actions)
          .pipe(
            tap(() => {
              this.payPeriods$ = this.ongoingPayrollsService.listOngoingPayPeriods();
              this.resetOperationCounts();
            }),
            switchMap(() => this.fetchPayrolls(true)),
            catchError(() => {
              this.executingActions = false;
              this.isSingleActionToggled = false;
              this.resetOperationCounts();
              return EMPTY;
            })
          )
          .subscribe();
      }
    }
  }


  private clearSelectionDetails() {
    this.massActionButtons.detail.forEach(detail => detail.count = 0);
    this.massActionButtons.total = 0;
    this.resetSelectedActionsMap();
  }

  private resetOperationCounts() {
    this.countDone = null;
    this.countFreeze = null;
    this.countUnfreeze = null;
    this.countLaunch = null;
    this.countReady = null;
    this.countNext = null;
  }


  private unSelectActionsFromSelectedPay(selectedId: number) {
    Enums.forEachValue<Action>(Action, action => {
      if (action !== Action.READY && action !== Action.CREATE_NEXT_PPC) {
        this.selectedPPCIdsByActionsMap.get(action).delete(selectedId);
      }
    });

  }

  private getExecutedActionsIds(action: Action) {
    if (this.selectedAction === action) {
      return this.isSingleActionToggled ? [this.selectedPay.id] : Array.from(this.selectedPPCIdsByActionsMap.get(action).values());
    }
    return [];
  }

  private updateRelevantCountBasedOnAction(action: Action, count: number) {
    if (Action.DONE === action) {
      this.countDone = count;
    } else if (Action.READY === action) {
      this.countReady = count;
    } else if (Action.CREATE_NEXT_PPC === action) {
      this.countNext = count;
    } else if (Action.LAUNCH === action) {
      this.countLaunch = count;
    } else if (Action.FIX === action) {
      this.countFreeze = count;
    } else if (Action.UNFREEZE === action) {
      this.countUnfreeze = count;
    }
  }


  public hasMoreThanOneActionTypeSelected(): boolean {
    const tempMap = cloneDeep(this.selectedPPCIdsByActionsMap);
    tempMap.delete(Action.NONE);
    return Array.from(tempMap.values()).filter(value => value.size > 0).length > 1 || this.allSelectedPaysHasMoreThanOnePossibleAction();
  }

  private allSelectedPaysHasMoreThanOnePossibleAction() {
    if (!this.allSelectionToggled) {
      return false;
    }
    let count = 0;
    if (this.getMassActionCount(Action.LAUNCH)) {
      count++;
    }
    if (this.getMassActionCount(Action.DONE)) {
      count++;
    }
    if (this.getMassActionCount(Action.FIX)) {
      count++;
    }
    if (this.getMassActionCount(Action.UNFREEZE)) {
      count++;
    }
    return count > 1;

  }


  public getSingleSelectedAction(): Action {
    const selectedActions: Action[] = [];
    this.selectedPPCIdsByActionsMap.forEach((value, key) => {
      if (key !== Action.NONE && value.size > 0) {
        selectedActions.push(key);
      }
    });
    if (selectedActions.length === 1 && !this.allSelectedPaysHasMoreThanOnePossibleAction()) {
      return selectedActions[0];
    }
    return null;
  }

  public getButtonLabelBasedOnAction(): string {
    const action = this.getSingleSelectedAction();
    if (action != null) {
      if (Action.LAUNCH === action) {
        return this.translate.instant('PAYROLL_ONGOING.launch_selected', {
          massActionCount: this.getSelectedActionsCount(action)
        });
      } else if (Action.DONE === action) {
        return this.translate.instant('PAYROLL_ONGOING.done_selected', {
          massActionCount: this.getSelectedActionsCount(action)
        });
      } else if (Action.FIX === action) {
        return this.translate.instant('PAYROLL_ONGOING.freeze_selected', {
          massActionCount: this.getSelectedActionsCount(action)
        });
      } else if (Action.UNFREEZE === action) {
        return this.translate.instant('PAYROLL_ONGOING.unfreeze_selected', {
          massActionCount: this.getSelectedActionsCount(action)
        });
      }
    }
    return this.translate.instant('COMMON.execute');
  }

}
